var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useEffect, useCallback } from "react";
import SvgCanvas from "@svgedit/svgcanvas";
import { svgCanvasConfig } from "./svgCanvasConfig";
import updateCanvas from "./updateCanvas";
export const useCanvasSetup = (imageUrl) => {
    const svgCanvasRef = useRef(null);
    const svgCanvasInstanceRef = useRef(null);
    const createSvgWithImage = useCallback((url) => __awaiter(void 0, void 0, void 0, function* () {
        const isSvg = url.toLowerCase().endsWith(".svg") || url.startsWith("data:image/svg+xml");
        if (isSvg) {
            try {
                let svgContent;
                if (url.startsWith("data:image/svg+xml")) {
                    const decoded = decodeURIComponent(url.split(",")[1] || url);
                    svgContent = decoded;
                }
                else {
                    const response = yield fetch(url);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
                    }
                    svgContent = yield response.text();
                }
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
                const svgElement = svgDoc.documentElement;
                if (svgElement.tagName === "svg") {
                    const viewBox = svgElement.getAttribute("viewBox");
                    let width = svgElement.getAttribute("width") || "800";
                    let height = svgElement.getAttribute("height") || "600";
                    width = width.replace(/px|em|rem|%/, "") || "800";
                    height = height.replace(/px|em|rem|%/, "") || "600";
                    const innerContent = Array.from(svgElement.children)
                        .filter((child) => {
                        const tagName = child.tagName.toLowerCase();
                        return tagName !== "title" && tagName !== "metadata" && tagName !== "desc";
                    })
                        .map((child) => child.outerHTML)
                        .join("");
                    let finalSvg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"`;
                    if (viewBox) {
                        finalSvg += ` viewBox="${viewBox}"`;
                    }
                    finalSvg += `>${innerContent}</svg>`;
                    return finalSvg;
                }
                return svgContent;
            }
            catch (error) {
                console.error("Error loading SVG:", error);
                return `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg"></svg>`;
            }
        }
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const svg = `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <image x="0" y="0" width="800" height="600" xlink:href="${url}" preserveAspectRatio="xMidYMid meet" />
        </svg>`;
                resolve(svg);
            };
            img.onerror = () => {
                const svg = `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg"></svg>`;
                resolve(svg);
            };
            img.src = url;
        });
    }), []);
    useEffect(() => {
        if (!svgCanvasRef.current)
            return;
        let isMounted = true;
        const initCanvas = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!isMounted || !svgCanvasRef.current)
                return;
            try {
                const initialSvg = yield createSvgWithImage(imageUrl);
                const config = Object.assign({}, svgCanvasConfig);
                const canvas = new SvgCanvas(svgCanvasRef.current, config);
                svgCanvasInstanceRef.current = canvas;
                const success = canvas.setSvgString(initialSvg.replace(/'/g, "\\'"), true);
                if (!success) {
                    console.error("Error loading SVG");
                    return;
                }
                updateCanvas({
                    svgCanvas: canvas,
                    cnvs: svgCanvasRef.current,
                    curConfig: config,
                    center: true,
                });
                setTimeout(() => {
                    canvas.setMode("select");
                    const svgRoot = canvas.getSvgRoot();
                    if (svgRoot) {
                        svgRoot.setAttribute("style", "pointer-events: none;");
                        const applyCursorStyles = (element) => {
                            if (element.tagName !== "image") {
                                const currentStyle = element.getAttribute("style") || "";
                                let newStyle = currentStyle.replace(/cursor:\s*[^;]+;?/g, "").trim();
                                if (!newStyle.includes("pointer-events")) {
                                    newStyle = `pointer-events: auto; ${newStyle}`.trim();
                                }
                                newStyle = `${newStyle}; cursor: pointer;`.trim();
                                element.setAttribute("style", newStyle);
                                // Add padding for easier selection by increasing stroke-width visually
                                const strokeWidth = element.getAttribute("stroke-width");
                                if (strokeWidth) {
                                    const width = parseFloat(strokeWidth);
                                    if (width > 0 && width < 10) {
                                        // Add invisible stroke for easier selection
                                        element.setAttribute("stroke-width", Math.max(width, 3).toString());
                                    }
                                }
                            }
                        };
                        Array.from(svgRoot.children).forEach((child) => {
                            if (child instanceof SVGElement) {
                                applyCursorStyles(child);
                            }
                        });
                        const updateCursorStyles = () => {
                            const root = canvas.getSvgRoot();
                            if (root) {
                                Array.from(root.children).forEach((child) => {
                                    if (child instanceof SVGElement) {
                                        applyCursorStyles(child);
                                    }
                                });
                            }
                        };
                        canvas.bind("changed", updateCursorStyles);
                    }
                }, 100);
                canvas.bind("selected", (_win, elems) => {
                    const svgRoot = canvas.getSvgRoot();
                    if (!svgRoot)
                        return;
                    // First, remove ALL previous selection highlights
                    const allElements = svgRoot.querySelectorAll("*");
                    allElements.forEach((elem) => {
                        if (elem instanceof SVGElement && elem.hasAttribute("data-selected-highlight")) {
                            elem.removeAttribute("data-selected-highlight");
                            const originalStroke = elem.getAttribute("data-original-stroke");
                            const originalStrokeWidth = elem.getAttribute("data-original-stroke-width");
                            // Restore original values
                            if (originalStroke !== null) {
                                elem.setAttribute("stroke", originalStroke);
                                elem.removeAttribute("data-original-stroke");
                            }
                            if (originalStrokeWidth !== null) {
                                elem.setAttribute("stroke-width", originalStrokeWidth);
                                elem.removeAttribute("data-original-stroke-width");
                            }
                        }
                    });
                    if (elems && elems.length > 0) {
                        // Check if SVG root is selected
                        const hasSvgRoot = elems.some((elem) => { var _a; return elem === svgRoot || ((_a = elem.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === "svg"; });
                        if (hasSvgRoot) {
                            setTimeout(() => canvas.clearSelection(), 0);
                            document.dispatchEvent(new CustomEvent("selectionchanged", { detail: { elements: [] } }));
                            return;
                        }
                        // Filter to only actual drawable elements (not SVG root, not image elements used as background)
                        const validElements = elems.filter((elem) => {
                            var _a, _b;
                            if (!(elem instanceof SVGElement))
                                return false;
                            if (elem === svgRoot)
                                return false;
                            if (((_a = elem.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === "svg")
                                return false;
                            if (((_b = elem.tagName) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === "image")
                                return false;
                            // Only include elements that are direct children of svgRoot or are actual drawable elements
                            return true;
                        });
                        // Highlight ONLY the exact elements in the selection array
                        validElements.forEach((elem) => {
                            if (elem instanceof SVGElement) {
                                // Store original stroke values BEFORE modifying (only if not already stored)
                                if (!elem.hasAttribute("data-original-stroke")) {
                                    const currentStroke = elem.getAttribute("stroke");
                                    if (currentStroke && currentStroke !== "#04D2C6") {
                                        elem.setAttribute("data-original-stroke", currentStroke);
                                    }
                                    else if (!currentStroke) {
                                        elem.setAttribute("data-original-stroke", "none");
                                    }
                                }
                                if (!elem.hasAttribute("data-original-stroke-width")) {
                                    const currentStrokeWidth = elem.getAttribute("stroke-width");
                                    if (currentStrokeWidth) {
                                        elem.setAttribute("data-original-stroke-width", currentStrokeWidth);
                                    }
                                    else {
                                        elem.setAttribute("data-original-stroke-width", "1");
                                    }
                                }
                                // Apply teal highlight
                                elem.setAttribute("data-selected-highlight", "true");
                                const originalStrokeWidth = elem.getAttribute("data-original-stroke-width") || "1";
                                const strokeWidth = parseFloat(originalStrokeWidth);
                                // Set stroke color to teal
                                elem.setAttribute("stroke", "#04D2C6");
                                // Make stroke wider for visibility
                                elem.setAttribute("stroke-width", Math.max(strokeWidth, 3).toString());
                            }
                        });
                        document.dispatchEvent(new CustomEvent("selectionchanged", { detail: { elements: validElements } }));
                    }
                    else {
                        document.dispatchEvent(new CustomEvent("selectionchanged", { detail: { elements: [] } }));
                    }
                });
            }
            catch (error) {
                console.error("Error initializing canvas:", error);
            }
        });
        requestAnimationFrame(() => {
            if (isMounted) {
                initCanvas();
            }
        });
        return () => {
            isMounted = false;
            if (svgCanvasInstanceRef.current) {
                svgCanvasInstanceRef.current = null;
            }
        };
    }, [imageUrl, createSvgWithImage]);
    return { svgCanvasRef, svgCanvasInstanceRef };
};
